import React, { createContext, useContext, useMemo } from "react";

import type { ReviewMenuTarget } from "../components/ContextMenu/reviewMenuItems";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Represents a persisted cell review, from MPX or from BTX field comments. */
export interface CellReview {
  id?: string;
  comment?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Present on BTX-backed records, whose row outlives the review it was created
   * for. Absent on MPX, where the record's existence is the flag.
   */
  needsReview?: boolean;
  /** Whether an analyst has validated the cell's value. */
  isValidated?: boolean;
}

export interface CellReviewContextValue {
  getReview: (planId: string, fieldPath: string) => CellReview | undefined;
  upsertReview: (
    planId: string,
    fieldPath: string,
    comment: string | null,
  ) => void;
  clearReview: (planId: string, fieldPath: string) => void;
  /**
   * Set or clear the validated flag, which names the state opposite a review:
   * each write clears the other.
   */
  setValidated: (targets: ReviewMenuTarget[], isValidated: boolean) => void;
  /** Replace the cell's comment, leaving its flags where they are. */
  setComment: (planId: string, fieldPath: string, comment: string) => void;
  getBrokerHighlight: (planId: string, fieldPath: string) => boolean;
  setBrokerHighlight: (planId: string, fieldPath: string) => void;
  clearBrokerHighlight: (planId: string, fieldPath: string) => void;
  /** Whether the cell carries the persisted "Edited" notation. */
  getEdited: (planId: string, fieldPath: string) => boolean;
  /** Persist the "Edited" notation on a manually-edited cell. */
  setEdited: (planId: string, fieldPath: string) => void;
  commentMaxLength?: number;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const NO_OP: CellReviewContextValue = {
  getReview: () => undefined,
  upsertReview: () => {},
  clearReview: () => {},
  setValidated: () => {},
  setComment: () => {},
  getBrokerHighlight: () => false,
  setBrokerHighlight: () => {},
  clearBrokerHighlight: () => {},
  getEdited: () => false,
  setEdited: () => {},
};

const CellReviewContext = createContext<CellReviewContextValue>(NO_OP);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface CellReviewProviderProps {
  getReview: (planId: string, fieldPath: string) => CellReview | undefined;
  upsertReview: (
    planId: string,
    fieldPath: string,
    comment: string | null,
  ) => void;
  clearReview: (planId: string, fieldPath: string) => void;
  /**
   * Omitted by a surface whose store has no validated flag to write — MPX cell
   * reviews carry no such column — which leaves those actions inert.
   */
  setValidated?: (targets: ReviewMenuTarget[], isValidated: boolean) => void;
  setComment?: (planId: string, fieldPath: string, comment: string) => void;
  getBrokerHighlight: (planId: string, fieldPath: string) => boolean;
  setBrokerHighlight: (planId: string, fieldPath: string) => void;
  clearBrokerHighlight: (planId: string, fieldPath: string) => void;
  getEdited: (planId: string, fieldPath: string) => boolean;
  setEdited: (planId: string, fieldPath: string) => void;
  commentMaxLength?: number;
  children: React.ReactNode;
}

/** Stands in for a write the mounting surface's store cannot perform. */
const ignore = (): void => {};

export const CellReviewProvider = ({
  getReview,
  upsertReview,
  clearReview,
  setValidated = ignore,
  setComment = ignore,
  getBrokerHighlight,
  setBrokerHighlight,
  clearBrokerHighlight,
  getEdited,
  setEdited,
  commentMaxLength,
  children,
}: CellReviewProviderProps): React.JSX.Element => {
  const value = useMemo<CellReviewContextValue>(
    () => ({
      getReview,
      upsertReview,
      clearReview,
      setValidated,
      setComment,
      getBrokerHighlight,
      setBrokerHighlight,
      clearBrokerHighlight,
      getEdited,
      setEdited,
      commentMaxLength,
    }),
    [
      getReview,
      upsertReview,
      clearReview,
      setValidated,
      setComment,
      getBrokerHighlight,
      setBrokerHighlight,
      clearBrokerHighlight,
      getEdited,
      setEdited,
      commentMaxLength,
    ],
  );

  return (
    <CellReviewContext.Provider value={value}>
      {children}
    </CellReviewContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Returns the CellReview context. Falls back to a no-op when no provider is mounted. */
export const useCellReviews = (): CellReviewContextValue =>
  useContext(CellReviewContext);
